import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
  type EnumOption,
} from '@resource-kit/model';
import type { ResourceBundle, DataTableParams } from '@resource-kit/controllers/react';
import type { components } from '@/types/api.generated';
import { http } from '@lib/api';
import { toListResult, type ListPage } from '@/services/list-envelope';
import type { ListResult } from '@resource-kit/controllers';
import { toApiListParams } from '@/services/list-request';
import { WEBHOOK_EVENT_FILTERS, WEBHOOK_EVENT_SORTS } from '@/services/filters.generated';
import i18n from '@/i18n';
import { PERMISSIONS } from '@/permissions';
import { lookupEnumLabelOrWarn } from '@/lib/enum-label';
import { WebhookPayloadCard } from './WebhookPayloadCard';
import { WebhookRecoveryCard } from './WebhookRecoveryCard';

// Wire shapes are generated from the backend routes (ADR-0006). The list
// endpoint (`GET /api/webhook-events`) returns `WebhookEventListItem`; the
// detail endpoint (`GET /api/webhook-events/{id}`) returns `WebhookEventDetail`
// which is the same row plus `headers` (event-routes extends the list schema
// with `headers: z.string()`). The resource works over the superset so a
// single model serves both surfaces; the detail-only extras are DERIVED from
// the generated detail component (#1405) — optional because list rows lack
// them — instead of a hand-written `headers` add-on.
//
// Authored against the resource-kit v2 model/view contracts and consumed
// directly as a `ResourceBundle` by `ResourcePage` (#1805 slice A2). The two
// `copyable` columns moved to `./widgets.tsx`; read its
// header before touching either — the swap that looks obvious is an export
// change, not a rename.
type WebhookEventListItem = components['schemas']['WebhookEventListItem'];
type WebhookEventDetail = components['schemas']['WebhookEventDetail'];
export type WebhookEvent = WebhookEventListItem &
  Partial<Omit<WebhookEventDetail, keyof WebhookEventListItem>>;
export type WebhookEventPayload = components['schemas']['WebhookEventPayload'];
export type WebhookEventReplayResult = components['schemas']['WebhookEventReplayResult'];

// The host slug, named once so the recovery card's cache invalidation and the
// host declaration below cannot drift into two different namespaces.
export const WEBHOOK_RESOURCE_SLUG = 'webhooks';

const webhookAPI = {
  // `toApiListParams` maps `DataTableParams.limit` to the kernel's reserved
  // `size` key (there is NO `limit` alias) and whitelists filters against the
  // generated `WEBHOOK_EVENT_FILTERS` — the same object the host declares as
  // `filterable`, so what the QueryBuilder offers and what this request
  // forwards are one set, not two that happen to agree. The default
  // `received_at:desc` sort passes through verbatim.
  list: async (params: DataTableParams): Promise<ListResult<WebhookEventListItem>> =>
    toListResult(
      await http.get<ListPage<WebhookEventListItem>>('/api/webhook-events', {
        params: toApiListParams(params, WEBHOOK_EVENT_FILTERS),
      }),
    ),
  get: (id: string): Promise<WebhookEventDetail> =>
    http.get<WebhookEventDetail>(`/api/webhook-events/${id}`),
  // Dedicated raw-payload surface (#1388) — kept off the detail row so the
  // multi-KB blob is only fetched when the detail surface renders. `thinned`
  // signals retention emptied an aged event's body (raw_payload === '').
  getPayload: (id: string): Promise<WebhookEventPayload> =>
    http.get<WebhookEventPayload>(`/api/webhook-events/${id}/payload`),
  // #2220: re-drive ONE stored event through the live ingestion core. Keyed by
  // the event id because a row stranded by a D1 overload has no
  // `webhook_failures` row for the older `/api/webhook-failures/{id}/replay`
  // entry point to start from.
  replay: (id: string): Promise<WebhookEventReplayResult> =>
    http.post<WebhookEventReplayResult>(`/api/webhook-events/${id}/replay`),
};

export const webhookEventsAPI = webhookAPI;

// `provider` is the backend's closed enum (`'shopify' | 'onfleet'`). Typing the
// option `value`s against the generated union makes a backend provider rename /
// addition fail compilation here rather than silently rendering a raw string.
const providerOptions: EnumOption[] = [
  { value: 'shopify', label: 'webhook.enum.shopify' },
  { value: 'onfleet', label: 'webhook.enum.onfleet' },
] satisfies ReadonlyArray<{ value: WebhookEvent['provider']; label: string }>;

/**
 * Closed set mirroring the backend's `WEBHOOK_EVENT_STATUS`
 * (`platform/webhook/store/schema.ts`): received → completed | failed.
 *
 * Declared as a const map rather than left as literals inside `statusOptions`
 * because a second consumer reads one of these values as a CONTRACT: the
 * recovery card (#2220) enables its Replay button only for `received`, which
 * is the same string the backend's own guard compares against. Two hand-typed
 * copies of that string across a component boundary is exactly the
 * magic-string contract the conventions forbid, and the failure mode is a
 * button that silently never enables.
 */
export const WEBHOOK_EVENT_STATUS = {
  RECEIVED: 'received',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

const statusOptions: EnumOption[] = [
  { value: WEBHOOK_EVENT_STATUS.RECEIVED, label: 'webhook.enum.received', color: 'secondary' },
  { value: WEBHOOK_EVENT_STATUS.COMPLETED, label: 'webhook.enum.completed', color: 'default' },
  { value: WEBHOOK_EVENT_STATUS.FAILED, label: 'webhook.enum.failed', color: 'destructive' },
];

// Subtitle separator pinned once so the render function and any test asserting
// subtitle output agree on the same em-dash (U+2014) instead of relying on
// IDE / paste / locale autocorrect (EQD §1 — no magic-string contracts).
const SUBTITLE_SEP = ' — ';

const f = createFieldConstructors<WebhookEvent>();

export const webhookModel = defineModel<WebhookEvent>()({
  name: 'webhook',
  idField: 'id',
  // The topic is what an operator calls an event. `recName` has no consumer on
  // the detail surface yet (the header title travels on the host — see
  // `packages/resource-kit-controllers/react/bundle.ts`); this is the value it
  // will resolve to when Stage B wires it up.
  recName: 'topic',
  fields: {
    // No widget — `type: 'id'` resolves to the id type's own projection with
    // the canonical list/detail truncation.
    id: f.id({ label: 'webhook.field.id' }),
    // Plain enum — the wire value is already the clean provider key, so the
    // generic enum renderer localizes it directly (no colon extraction).
    provider: f.enum({ label: 'webhook.field.provider', options: providerOptions }),
    topic: f.text({ label: 'webhook.field.topic' }),
    status: f.enum({ label: 'webhook.field.status', options: statusOptions }),
    // FORENSIC (#2105): every timestamp on this resource is read as a UTC day,
    // not a merchant business day. A webhook row exists to be correlated with
    // the provider's own log and with an incident timeline, both of which are
    // UTC; shifting the filter window to a merchant calendar would put the
    // console and the provider dashboard on different days for the same
    // search. The `instantReading` on each column below says so per column,
    // because that is where the serializer reads it from.
    received_at: f.datetime({ label: 'webhook.field.receivedAt', instantReading: 'utc' }),
    // #1291 — declarative: `type: 'id'` already dispatches to the id projection
    // with the canonical truncation + copy button (was a hand-rolled IdCell
    // render override). Empty value degrades to its own dash.
    trace_id: f.id({ label: 'webhook.field.traceId' }),
    tenant_id: f.text({ label: 'webhook.field.tenantId' }),
    api_version: f.text({ label: 'webhook.field.apiVersion' }),
    // #1291 — provider event id is an external cross-reference key, so it
    // carries the copy affordance. It stays `text`, NOT `id`: the TYPE is what
    // the CSV export falls through to once the widget declares no `csvText`,
    // and the id type exports an empty value as `''` where this column must
    // keep exporting `-`. See `./widgets.tsx`.
    provider_event_id: f.text({
      label: 'webhook.field.providerEventId',
      widget: 'webhookCopyableText',
    }),
    provider_emitted_at: f.datetime({
      label: 'webhook.field.providerEmittedAt',
      instantReading: 'utc',
    }),
    last_attempt_at: f.datetime({ label: 'webhook.field.lastAttemptAt', instantReading: 'utc' }),
    completed_at: f.datetime({ label: 'webhook.field.completedAt', instantReading: 'utc' }),
    // #1291 — payload hash is copied for dedup/idempotency debugging. Same
    // `text`-not-`id` argument as `provider_event_id` directly above.
    payload_hash: f.text({
      label: 'webhook.field.payloadHash',
      widget: 'webhookCopyableText',
    }),
    headers: f.json({ label: 'webhook.field.headers' }),
  },
});

export const webhookListView = defineListView(webhookModel, {
  items: [
    { field: 'id' },
    { field: 'provider' },
    { field: 'topic' },
    { field: 'status' },
    { field: 'received_at' },
    { field: 'trace_id', columnInvisible: true },
    { field: 'tenant_id', columnInvisible: true },
    { field: 'api_version', columnInvisible: true },
    { field: 'provider_event_id', columnInvisible: true },
    { field: 'provider_emitted_at', columnInvisible: true },
    { field: 'last_attempt_at', columnInvisible: true },
    { field: 'completed_at', columnInvisible: true },
    { field: 'payload_hash', columnInvisible: true },
    { field: 'headers', columnInvisible: true },
  ],
  defaultSort: { field: 'received_at', dir: 'desc' },
  // No `capabilities` block at all, which is two separate statements.
  //
  // `search` is deliberately absent, not `false`: the definition never declared
  // `searchable`, and `resource-searchable-parity.test.ts` holds webhook events
  // in the set of routes that do not search.
  //
  // `filter` no longer needs one either. It carried `filter: true` until #2114
  // to hold the wide filter row open while the host declared no whitelist; now
  // that the host binds `WEBHOOK_EVENT_FILTERS`, `resolveListCapabilities`
  // answers `true` from the whitelist itself (`listCapabilities.ts`,
  // `sourceDefault` case `'filter'`: a `remote` list offers the builder when
  // the whitelist is non-empty). An override here would say nothing the
  // whitelist does not already say — and would keep saying it if the whitelist
  // ever emptied.
});

export const webhookFormView = defineFormView(webhookModel, {
  groupDisplay: 'tab',
  children: [
    {
      kind: 'fields',
      id: 'main',
      label: 'webhook.group.main',
      items: [
        { field: 'id' },
        { field: 'provider' },
        { field: 'topic' },
        { field: 'status' },
        { field: 'trace_id' },
        { field: 'tenant_id' },
      ],
    },
    {
      kind: 'fields',
      id: 'timing',
      label: 'webhook.group.timing',
      items: [
        { field: 'received_at' },
        { field: 'provider_emitted_at' },
        { field: 'last_attempt_at' },
        { field: 'completed_at' },
      ],
    },
    {
      kind: 'fields',
      id: 'content',
      label: 'webhook.group.content',
      items: [
        { field: 'api_version' },
        { field: 'provider_event_id' },
        { field: 'payload_hash' },
        { field: 'headers' },
      ],
    },
    // #1388: the raw payload is a dedicated admin-gated endpoint (never on the
    // list/detail rows). The tab lives on `ResourceDetailPanel`'s rail, so BOTH
    // the drawer and the `/webhooks/:id` page auto-load and render it.
    {
      kind: 'custom',
      id: 'payload',
      label: 'webhook.payload.title',
      render: function WebhookPayloadTab({ record }) {
        return <WebhookPayloadCard eventId={record.id} />;
      },
    },
    // #2220: the operator's single-row reach into a delivery stranded at
    // `received` by a D1 overload (ADR-0036 decision B). It sits on the same
    // rail as the payload, so it is one click away on the drawer and the page.
    {
      kind: 'custom',
      id: 'recovery',
      label: 'webhook.recovery.title',
      render: function WebhookRecoveryTab({ record }) {
        return <WebhookRecoveryCard eventId={record.id} status={record.status} />;
      },
      // The POST behind this tab is `webhook_failures:write` server-side, so a
      // read-only operator must not be shown a control that can only 403.
      // `gated: true` declares only that a gate EXISTS (#1917); the verdict is
      // an identity fact and travels on the host below.
      gated: true,
    },
  ],
});

export const webhookResource: ResourceBundle<WebhookEvent, typeof webhookModel.fields> = {
  model: webhookModel,
  list: webhookListView,
  form: webhookFormView,
  host: {
    api: {
      // Both verbs answer the shared surface's own vocabulary: `webhookAPI.list`
      // renames the kernel's `size` at its own definition (#2167 PR-2) and the
      // detail record needs no envelope at all.
      list: (params) => webhookAPI.list(params),
      get: (id): Promise<WebhookEvent> => webhookAPI.get(id),
    },
    label: 'webhook.title',
    slug: WEBHOOK_RESOURCE_SLUG,
    listTitle: 'webhook.listTitle',
    listDescription: 'webhook.listDescription',
    searchPlaceholder: 'webhook.searchPlaceholder',
    // Mirrors `EVENT_LIST_CONFIG.sortable` via the generated whitelist (#1639):
    // this is a server-paginated list, so a header outside the set 400s the
    // whole inbox. The binding is what disables those headers.
    //
    // `filterable` is the same binding for the filter axis, and it is NEW in
    // #2114 (owner decision, 2026-08-19). This resource used to omit it on
    // purpose, on the grounds that declaring it would narrow the operator's
    // filter row. It would — that narrowing is the point. Without the
    // declaration `deriveQueryColumnsFromFields` leaves each column's
    // `operations` undefined, the QueryBuilder offers the full operator table
    // over all fourteen columns, and `toApiListParams` then drops every
    // condition the route does not accept (`services/list-request.ts`
    // `isWhitelisted`) — silently, because `droppedFilterFields` returns `[]`
    // when there is no whitelist to compare against. The row was wide because
    // it was unchecked, not because those filters worked.
    //
    // The set is the backend's own: `EVENT_LIST_CONFIG.filterable` in
    // `backend/src/platform/webhook/event-routes.ts` (provider / topic /
    // status, `eq` only), reaching this file through `x-filterable` codegen.
    // Binding the generated constant rather than a local literal is guardrail A
    // of #1837 (`__tests__/architecture/resource-filterable-binding.test.ts`);
    // `webhookAPI.list` passes the very same object, so the offered filters and
    // the forwarded ones cannot drift apart.
    sortable: WEBHOOK_EVENT_SORTS,
    filterable: WEBHOOK_EVENT_FILTERS,
    staleTime: 30 * 1000,
    detail: {
      title: (item) => i18n.t('webhook.field.topic') + ': ' + item.topic,
      // Subtitle is a string-only surface (cannot carry a destructive Badge
      // like the list cell can), so the drift signal is the
      // `[unknown:${value}]` marker from `lookupEnumLabelOrWarn` (EQD §3 —
      // silent fallback forbidden).
      subtitle: (item) => {
        const providerLabel = lookupEnumLabelOrWarn(
          item.provider,
          providerOptions,
          'webhook provider',
        );
        const statusLabel = lookupEnumLabelOrWarn(item.status, statusOptions, 'webhook status');
        return `${providerLabel}${SUBTITLE_SEP}${statusLabel}`;
      },
      // #770: the manual retry entry-point was removed — the rebuilt core
      // ingests webhooks synchronously (ADR-0002), so there is no async path
      // to re-drive a retry through. Operator-driven replay lives on the
      // separate webhook-failures inbox
      // (`/api/webhook-failures/{id}/replay`, #935 PR-D) and, for a row that
      // inbox cannot reach, on the #2220 recovery tab gated below.
      //
      // The verdict for the one gated tab this form declares. It mirrors the
      // backend route (`PERMISSIONS.WEBHOOK_FAILURES_WRITE` on
      // `POST /api/webhook-events/{id}/replay`) rather than inventing a
      // frontend-only rule. `isAdmin` stays in the disjunction because the
      // backend's `requirePermission` short-circuits on the admin role: an
      // admin reaches the route without holding the key, so a grant-only check
      // here would hide a tab the API would have served.
      tabGates: ({ isAdmin, hasGrant }) => ({
        recovery: isAdmin || hasGrant(PERMISSIONS.WEBHOOK_FAILURES_WRITE),
      }),
    },
  },
};
